import { Link, Outlet, useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/utils/cn';

import { ParameterCategoryFilter } from '../components/parameters/parameter-category-filter';
import { useConfigurationScope } from '../hooks/useConfigurationScope';
import type { ConfigurationPlatform } from '../types/configuration.types';

/** Platforms we show in the dropdown — only GMC has data in the MSW mock today. */
const PLATFORM_OPTIONS: { value: ConfigurationPlatform; disabled: boolean }[] = [
  { value: 'GMC', disabled: false },
  { value: 'META', disabled: true },
  { value: 'TIKTOK', disabled: true },
];

/**
 * Shell for the Configuration Manager pages (`/configuration/manager/parameters`
 * and `/configuration/manager/audit-trail`). Renders the page header, the
 * Parameters / Audit Trail tab group, the Platform dropdown, and an Outlet for
 * the active child. Platform lives in a URL search param so it survives tab
 * switches, refresh, and bookmarks. Used by the `manager.tsx` layout —
 * Experiment Configuration does NOT mount this, it ships its own page chrome.
 */
export function ConfigurationManagerLayout() {
  const { t } = useTranslation('configuration');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/_tenant/configuration' });
  const platform = search.platform;
  const scope = useConfigurationScope();

  const isAuditTrail = pathname.endsWith('/audit-trail');

  const setPlatform = (next: ConfigurationPlatform) => {
    void navigate({
      to: pathname,
      search: { ...search, platform: next },
      replace: true,
    });
  };

  return (
    // `flex h-full min-h-0 flex-col` bounds this layout to the content lane's
    // height so the Outlet below (`flex-1 min-h-0`) can hand its own internal
    // scroll to the active tab instead of the whole page scrolling — the
    // header and toolbar stay fixed in place.
    <div className="flex h-full min-h-0 flex-col p-6 lg:p-8">
      {/* Header row — title + subtitle. In subsidiary mode the subtitle
         names the active subsidiary so it's obvious which parameter set
         (global vs this tenant's own) the page is showing. */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {scope.mode === 'subsidiary'
            ? t('subsidiaryScope.subtitle', { name: scope.subsidiaryName })
            : t('subtitle')}
        </p>
      </div>

      {/* Toolbar — stacks on mobile so neither the toggle nor the platform
         dropdown overflow. On lg+ the toggle aligns to the rail below it and
         the platform dropdown pins to the right. */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <SlidingTabsNav
          aria-label={t('tabs.a11y.navLabel')}
          className="w-full bg-white lg:w-72 xl:w-80"
        >
          <TabLink
            to="/configuration/manager/parameters"
            active={!isAuditTrail}
            label={t('tabs.parameters')}
          />
          <TabLink
            to="/configuration/manager/audit-trail"
            active={isAuditTrail}
            label={t('tabs.auditTrail')}
          />
        </SlidingTabsNav>
        <div className="flex items-center gap-2 self-end lg:self-auto">
          {/* Category filter — the compact, searchable stand-in for the desktop
             CategoryRail. Only meaningful on the Parameters tab, and only shown
             below `xl` (<1280px) where the rail is hidden. */}
          {!isAuditTrail && <ParameterCategoryFilter platform={platform} className="xl:hidden" />}
          <Label htmlFor="configuration-platform" className="text-muted-foreground text-sm">
            {t('platform.label')}
          </Label>
          <Select
            value={platform}
            onValueChange={(v: ConfigurationPlatform) => {
              setPlatform(v);
            }}
          >
            <SelectTrigger id="configuration-platform" className="w-40 cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLATFORM_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  className={opt.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                >
                  {t(`platforms.${opt.value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

interface SlidingTabsNavProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
}

/**
 * Router-navigation equivalent of the shared `TabsList` sliding pill.
 * Measures the child `[aria-current="page"]` link and animates an absolutely-
 * positioned indicator to it — identical visual behaviour to `TabsList` but
 * driven by URL state instead of Radix Tabs state.
 */
function SlidingTabsNav({ children, className, ...props }: SlidingTabsNavProps) {
  const listRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    function measure() {
      const active = list?.querySelector<HTMLElement>('[aria-current="page"]');
      if (!active) return;
      setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
    }

    measure();

    const mo = new MutationObserver(measure);
    mo.observe(list, { attributes: true, attributeFilter: ['aria-current'], subtree: true });

    const ro = new ResizeObserver(measure);
    ro.observe(list);

    return () => {
      mo.disconnect();
      ro.disconnect();
    };
  }, []);

  return (
    <nav
      ref={listRef}
      className={cn(
        'tab-border relative inline-flex h-auto items-center justify-center rounded-lg border-2 p-1',
        className,
      )}
      {...props}
    >
      {indicator && (
        <span
          aria-hidden="true"
          className="absolute top-1 bottom-1 rounded-lg bg-[#445CE4] shadow-sm transition-all duration-300 ease-out"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
      {children}
    </nav>
  );
}

interface TabLinkProps {
  to: '/configuration/manager/parameters' | '/configuration/manager/audit-trail';
  active: boolean;
  label: string;
}

function TabLink({ to, active, label }: TabLinkProps) {
  return (
    <Link
      to={to}
      search={(prev: Record<string, unknown>) => prev}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'focus-visible:ring-ring text-muted-foreground relative z-10 flex-1 cursor-pointer rounded-lg px-5 py-2 text-center text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        active ? 'text-white' : 'hover:text-foreground',
      )}
    >
      {label}
    </Link>
  );
}
